import React, { useRef } from "react";
import { toast } from "react-toastify";
import { usePlaidLink } from "react-plaid-link";
import { useFormik } from "formik";
import { useSelector } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import { type DialogProps } from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import MuiDialogTitle, {
  type DialogTitleProps,
} from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import Dialog from "src/components/Dialog";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import ZonedKeyboardDatePicker from "src/components/ZonedKeyboardDatePicker";

import LoadableButton from "src/components/LoadableButton";
import {
  usePlaid,
  transferPlaidPublicToken,
  type PlaidIntegrationType,
} from "src/apiService/modules/plaid";
import { bankAccountsWithOutIntegrationSelector } from "src/store/bankAccount/selector";
import { completeUserSetupQuestionCardBank } from "src/apiService/modules/users";
import { getTourStepType } from "src/store/tour/selector";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import { getCallableErrorMessage } from "src/utils/callableErrors";
import { startOfCalendarDayContainingInstant } from "src/utils/timezone";

export interface PlaidAddIntegrationDialogContentProps {
  DialogTitle: React.ComponentType<DialogTitleProps>;
  onFinish: (name: string) => void;
  onGoBack: () => void;
  type: PlaidIntegrationType;
  redirect?: string;
}

interface Values {
  bankAccountId: string;
  bankAccountType?: string;
  name?: string;
  date?: Date | null;
}

const Now = new Date();
const MinDate = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 2);
  return d;
})();

const useStyles = makeStyles((theme) => ({
  content: {
    "& > * + *": {
      marginTop: theme.spacing(2),
    },
  },
}));

export function PlaidAddIntegrationDialogContent({
  onFinish,
  onGoBack,
  DialogTitle,
  type,
  redirect,
}: PlaidAddIntegrationDialogContentProps) {
  const tourStep = useSelector(getTourStepType);
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  let handlePlaidSubmit: Function = () => {};
  const bankAccounts = useSelector(bankAccountsWithOutIntegrationSelector);
  const classes = useStyles();
  const formik = useFormik({
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      bankAccountId: "add-new",
      name: "",
      bankAccountType: "",
      date: null,
    } as Values,
    validate: (values) => {
      const errors: Record<string, string> = {};

      if (!values.name || !values.name.trim()) {
        errors.name = "Required value";
      }

      if (
        !values.date ||
        !values.date.getTime ||
        isNaN(values.date.getTime())
      ) {
        errors.date = "Required value";
      }

      if (!values.bankAccountType) {
        errors.bankAccountType = "Required value";
      }

      return errors;
    },
    onSubmit: (values, { setSubmitting }) => {
      if (tourStep === "connect-bank") {
        completeUserSetupQuestionCardBank();
      }
      handlePlaidSubmit();
    },
  });
  const formikRef = useRef(formik);
  formikRef.current = formik;
  const { linkToken, error: linkTokenError } = usePlaid({
    type,
    name: formik.values.name,
    date: formik.values.date || undefined,
    redirect,
    bankAccountId: formik.values.bankAccountId,
    bankAccountType: formik.values.bankAccountType,
  });
  const { open: openPlaid } = usePlaidLink({
    onSuccess: async (publicToken) => {
      if (!formikRef.current.values.name) {
        toast.error("You must provide a name");
        formikRef.current.setSubmitting(false);
        return;
      }
      try {
        await transferPlaidPublicToken(publicToken, type, {
          name: formikRef.current.values.name,
          date: formikRef.current.values.date || undefined,
          bankAccountId: formikRef.current.values.bankAccountId || "add-new",
          bankAccountType:
            formikRef.current.values.bankAccountType || "bank-account",
        });
        onFinish(formikRef.current.values.name);
      } catch (error) {
        toast.error(getCallableErrorMessage(error));
        formikRef.current.setSubmitting(false);
      }
    },
    onExit: (err) => {
      formikRef.current.setSubmitting(false);
      console.log(err);
      if (err) {
        console.log(`Plaid encounters an error: ${err}`);
        toast.error(err.toString());
      }
    },
    token: linkToken || null,
  });
  handlePlaidSubmit = openPlaid;

  return (
    <form onSubmit={formik.handleSubmit} autoComplete="off">
      <DialogTitle>Add New Credit Card/Bank Account</DialogTitle>
      <MuiDialogContent>
        <DialogContentText>
          You can integrate a credit card or bank account to pull your
          transaction data into My Reseller Genie. Attach the integration to an
          existing bank account or create a new one. Set a start date and we’ll
          pull as far back as the bank lets us. Once you’re ready to connect,
          click “Connect” to sign into your account.
        </DialogContentText>
        {linkTokenError ? (
          <DialogContentText color="error">{linkTokenError}</DialogContentText>
        ) : null}
        <div className={classes.content}>
          <TextField
            label="Bank / Credit Card"
            select
            variant="outlined"
            fullWidth
            name="bankAccountId"
            value={formik.values.bankAccountId}
            onChange={(ev) => {
              const value = ev.target.value;
              if (value === "add-new") {
                formik.setValues({
                  ...formik.values,
                  bankAccountId: "add-new",
                  name: "",
                  bankAccountType: "",
                });
                return;
              }

              const bankAccount = bankAccounts.find((ba) => ba.id === value);
              if (!bankAccount) return;
              formik.setValues({
                ...formik.values,
                bankAccountId: value,
                name: bankAccount.name,
                bankAccountType: bankAccount.type,
              });
            }}
          >
            <MenuItem value="add-new">Add New</MenuItem>
            {bankAccounts.map((ba) =>
              ba.type === "sale-platform" ? null : (
                <MenuItem key={ba.id} value={ba.id}>
                  {ba.name}
                </MenuItem>
              )
            )}
          </TextField>
          <TextField
            label="Account Name"
            variant="outlined"
            error={!!formik.errors.name}
            helperText={formik.errors.name}
            value={formik.values.name}
            name="name"
            onChange={formik.handleChange}
            disabled={
              formik.values.bankAccountId !== "add-new" || formik.isSubmitting
            }
            required
            fullWidth
          />
          <ZonedKeyboardDatePicker
            fullWidth
            variant="inline"
            inputVariant="outlined"
            disableToolbar
            format="MM/dd/yyyy"
            label="Start Date"
            required
            autoOk
            maxDate={Now}
            minDate={MinDate}
            error={!!formik.errors.date}
            helperText={formik.errors.date}
            value={formik.values.date}
            disabled={formik.isSubmitting}
            onChange={(d) => {
              if (!d) {
                formik.setFieldValue("date", null);
                return;
              }
              formik.setFieldValue(
                "date",
                startOfCalendarDayContainingInstant(d, timeZone)
              );
            }}
          />
          <TextField
            label="Account Type"
            select
            variant="outlined"
            fullWidth
            name="bankAccountType"
            value={formik.values.bankAccountType || ""}
            onChange={formik.handleChange}
            disabled={
              formik.values.bankAccountId !== "add-new" || formik.isSubmitting
            }
          >
            {formik.values.bankAccountId !== "add-new" ? (
              <MenuItem value="sale-platform">Sale Platform</MenuItem>
            ) : null}
            <MenuItem value="bank-account">Bank Account</MenuItem>
            <MenuItem value="credit-card">Credit Card</MenuItem>
          </TextField>
        </div>
      </MuiDialogContent>
      <DialogActions>
        <LoadableButton
          variant="contained"
          color="primary"
          autoFocus
          type="submit"
          loading={formik.isSubmitting}
          disabled={
            !linkToken ||
            !!linkTokenError ||
            !formik.values.name ||
            !formik.values.date
          }
        >
          Connect
        </LoadableButton>
      </DialogActions>
    </form>
  );
}

export interface PlaidAddIntegrationDialogProps {
  DialogTitle?: React.ComponentType<DialogTitleProps>;
  onClose: ((
    event: {},
    reason: "backdropClick" | "escapeKeyDown" | "onBack"
  ) => void) &
    ((event: {}, reason: "onFinish", accountName: string) => void);
}

function PlaidAddIntegrationDialog({
  DialogTitle = MuiDialogTitle,
  ...props
}: PlaidAddIntegrationDialogProps & Omit<DialogProps, "onClose">) {
  return (
    <Dialog
      TransitionProps={
        {
          tabIndex: "none",
        } as object
      }
      disableEnforceFocus
      {...props}
    >
      <PlaidAddIntegrationDialogContent
        DialogTitle={DialogTitle}
        onGoBack={() => props.onClose({}, "onBack")}
        onFinish={(name) => props.onClose({}, "onFinish", name)}
        type="expense"
      />
    </Dialog>
  );
}

export default PlaidAddIntegrationDialog;
